import { Alert, Spin, Typography } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import { AppButton } from 'shared/UI/UIKit/Button';
import { AppFlex } from 'shared/UI/UIKit/Flex';
import { AppForm, AppFormItem, formRules } from 'shared/UI/UIKit/Form';
import { AppInput } from 'shared/UI/UIKit/Input';
import { AppTable } from 'shared/UI/UIKit/Table';
import { AppCheckbox } from 'shared/UI/UIKit/Checkbox';
import { AppSelect } from 'shared/UI/UIKit/Select';
import { AppDivider } from 'shared/UI/UIKit/Divider';
import { ScheduleConstraintCode } from 'services/api';
import {
  isConstraintChecked,
  scheduleConstraintLabels,
  useEducationClassesTable,
  useScheduleConfiguration,
  type EducationClassRow,
} from '../model';
import styles from './styles.module.scss';
import {
  ConstraintWeightsModal,
  DifficultyDistributionModelInfo,
  MaxLessonsPopup,
} from './components';

export const ScheduleConfiguration = () => {
  const {
    state: {
      form,
      readOnly,
      loading,
      initialLoadDone,
      constraints,
      educationClassRows,
      isEducationClassesInvalid,
      allConstraintsChecked,
      checkedConstraintsCount,
      indeterminate,
      maxLessonsConfigurations,
      maxLessonsPerDayId,
      selectedMaxLessonsConfiguration,
      allConstraints,
      logicalConstraints,
      sanpinWeightConstraints,
      constraintWeights,
      constraintWeightsOpen,
    },
    methods: {
      handleSubmit,
      setEducationClassRows,
      handleCheckAll,
      handleConstraintItemChange,
      handleMaxLessonsPerDayChange,
      setConstraintWeights,
      handleOpenConstraintWeightModal,
      handleCloseConstraintWeightModal,
    },
  } = useScheduleConfiguration();

  const {
    state: { columns, dataSource },
    methods: { handleAddRow },
  } = useEducationClassesTable(educationClassRows, setEducationClassRows, readOnly);

  if (!initialLoadDone && loading) {
    return (
      <AppFlex justifyContent="center" style={{ padding: 48 }}>
        <Spin size="large" />
      </AppFlex>
    );
  }

  return (
    <AppForm form={form} onSubmit={handleSubmit} className={styles.formRoot}>
      <AppFlex direction="column" className={styles.form}>
        <AppFlex
          direction="row"
          justifyContent="space-around"
          className={styles.formContentWrapper}
          unit="35"
        >
          <AppFlex direction="column" unit="16" className={styles.configurationParameters}>
            <Typography.Text strong className={styles.formButton}>
              Параметры расписания
            </Typography.Text>
            <AppFormItem name="name" label="Наименование" required rules={formRules.required}>
              <AppInput disabled={readOnly} />
            </AppFormItem>
            <AppFormItem
              name="maxLessonsPerDayId"
              label="Максимальное количество уроков в день"
              required
            >
              <AppFlex direction="row" unit="8" alignItems="center">
                <AppSelect
                  options={maxLessonsConfigurations.map((c) => ({ label: c.name, value: c.id }))}
                  value={maxLessonsPerDayId ?? undefined}
                  onChange={handleMaxLessonsPerDayChange}
                  disabled={readOnly}
                />
                <MaxLessonsPopup configuration={selectedMaxLessonsConfiguration} />
              </AppFlex>
            </AppFormItem>

            {isConstraintChecked(
              constraints,
              ScheduleConstraintCode.DAILY_DIFFICULTY_DISTRIBUTION_CONFLICT,
            ) && (
              <AppFormItem
                name="difficultyDistributionModelCode"
                label={<DifficultyDistributionModelInfo />}
              >
                <AppSelect
                  options={[
                    { label: 'Горка', value: 'HILL' },
                    { label: 'Змейка', value: 'SNAKE' },
                  ]}
                  style={{ width: 150 }}
                  disabled={readOnly}
                />
              </AppFormItem>
            )}
            <AppFlex direction="column" unit="16">
              <AppFlex unit="4">
                <Typography.Text strong className={styles.formButton}>
                  Выбрать классы:
                </Typography.Text>
                <AppTable
                  columns={columns}
                  dataSource={dataSource}
                  rowKey={(record: EducationClassRow) => record.key}
                  size="small"
                />
                <div style={{ marginTop: 16 }}>
                  <AppButton
                    onClick={handleAddRow}
                    type="outline"
                    htmlType="button"
                    disabled={readOnly}
                  >
                    Добавить строку
                  </AppButton>
                </div>
                {isEducationClassesInvalid && (
                  <Typography.Text type="danger">
                    Необходимо выбрать хотя бы один класс
                  </Typography.Text>
                )}
              </AppFlex>
            </AppFlex>
          </AppFlex>
          <AppDivider vertical className={styles.divider} />
          <AppFlex direction="column" unit="30" className={styles.configurationConstraints}>
            <AppFlex direction="row" alignItems="center" justifyContent="space-between">
              <Typography.Text strong className={styles.formButton}>
                <SettingOutlined /> Ограничения расписания
              </Typography.Text>
              <AppButton type="outline" htmlType="button" onClick={handleOpenConstraintWeightModal}>
                Вес ограничений
              </AppButton>
            </AppFlex>
            <div className={styles.list}>
              <div className={styles.checkAllRow}>
                <AppCheckbox
                  checked={allConstraintsChecked}
                  indeterminate={indeterminate}
                  onChange={handleCheckAll}
                  disabled={readOnly}
                >
                  Выбрать все
                </AppCheckbox>
              </div>
              <div className={styles.items}>
                {allConstraints.map((constraint) => (
                  <div key={constraint.code} className={styles.item}>
                    <AppCheckbox
                      checked={isConstraintChecked(constraints, constraint.code)}
                      onChange={(e) =>
                        handleConstraintItemChange(constraint.code, e.target.checked)
                      }
                      disabled={readOnly}
                    >
                      {scheduleConstraintLabels[constraint.code]}
                    </AppCheckbox>
                  </div>
                ))}
              </div>
            </div>
            <Typography.Text>Выбрано ограничений: {checkedConstraintsCount}</Typography.Text>
            {allConstraintsChecked && !readOnly && (
              <Alert
                message="Генерация с заданными параметрами может занять некоторое время"
                type="warning"
                className={styles.warning}
                showIcon
              />
            )}
            <Alert
              type="info"
              showIcon
              className={styles.infoAlert}
              message={
                <AppFlex direction="column" unit="2">
                  <Typography.Title level={4}>Предварительно закреплённые уроки</Typography.Title>
                  <Typography.Text>
                    Урок &quot;Разговоры о важном&quot; закреплён первым уроком для всех классов.
                  </Typography.Text>
                  <Typography.Text>
                    Урок &quot;Россия мои горизонты&quot; закреплён в четверг последним уроком для
                    6-х классов и старше.
                  </Typography.Text>
                  <Typography.Text>
                    Убедитесь, что для этих уроков заданы соответствующие кабинеты и преподаватели.
                  </Typography.Text>
                </AppFlex>
              }
            />
          </AppFlex>
        </AppFlex>
        {!readOnly && (
          <AppFlex style={{ marginTop: 28, alignSelf: 'flex-end' }}>
            <AppButton
              type="primary"
              htmlType="button"
              onClick={handleSubmit}
              size="large"
              style={{ width: 350 }}
            >
              Сохранить
            </AppButton>
          </AppFlex>
        )}
        <ConstraintWeightsModal
          open={constraintWeightsOpen}
          onClose={handleCloseConstraintWeightModal}
          onSave={setConstraintWeights}
          logicalConstraints={logicalConstraints}
          sanpinConstraints={sanpinWeightConstraints}
          weights={constraintWeights}
          readOnly={readOnly}
        />
      </AppFlex>
    </AppForm>
  );
};
